import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { SupportMember } from "../../interfaces/Messages/chatTypes";
import type { SupportMemberDoc, RolesConfig } from "../../services/Messages/firestore";

interface AuthState {
  // Current user's support doc (fetched at login; used for auth + role updates)
  currentUserSupportDoc: SupportMemberDoc | null;
  setCurrentUserSupportDoc: (doc: SupportMemberDoc | null) => void;

  // Support team members (no longer populated at login; kept for compatibility)
  supportMembers: SupportMember[];
  setSupportMembers: (members: SupportMember[]) => void;

  // User roles (synced from Firebase)
  userRoles: string[];
  setUserRoles: (roles: string[]) => void;

  // Roles config from Firebase (authorized roles per channel type)
  rolesConfig: RolesConfig | null;
  setRolesConfig: (config: RolesConfig | null) => void;

  // Active role for the current channel (first matching role from user's roles)
  activeRole: string | null;
  setActiveRole: (role: string | null) => void;

  // Clear all auth state
  clearAuth: () => void;

  // Helpers
  getSupportMemberByCode: (code: number | string) => SupportMember | undefined;
  isUserInSupportTeam: (userId: number | string) => boolean;
}

/**
 * Zustand store for auth-related state with persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      currentUserSupportDoc: null,
      supportMembers: [],
      userRoles: [],
      rolesConfig: null,
      activeRole: null,

      setCurrentUserSupportDoc: (doc) => set({ currentUserSupportDoc: doc }),

      setSupportMembers: (members) => set({ supportMembers: members }),

      setUserRoles: (roles) => set({ userRoles: roles }),

      setRolesConfig: (config) => set({ rolesConfig: config }),

      setActiveRole: (role) => set({ activeRole: role }),

      clearAuth: () =>
        set({ supportMembers: [], userRoles: [], currentUserSupportDoc: null, rolesConfig: null, activeRole: null }),

      getSupportMemberByCode: (code) => {
        const members = get().supportMembers;
        const current = get().currentUserSupportDoc;
        if (current && String(current.id) === String(code)) {
          return { code: Number(current.id), name: current.name, roles: current.roles };
        }
        return members.find((m) => String(m.code) === String(code));
      },

      isUserInSupportTeam: (userId) => {
        const current = get().currentUserSupportDoc;
        if (current && String(current.id) === String(userId)) return true;
        const members = get().supportMembers;
        return members.some((m) => String(m.code) === String(userId));
      },
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentUserSupportDoc: state.currentUserSupportDoc,
        supportMembers: state.supportMembers,
        userRoles: state.userRoles,
        rolesConfig: state.rolesConfig,
        activeRole: state.activeRole,
      }),
    }
  )
);
